import { useIsMounted } from '#pplx/frontend/common/hooks/useIsMounted.js';
import { useCallback, useEffect, useRef, useState } from 'react';
// Default duration the `copied` flag stays true after a successful copy.
const DEFAULT_COPY_FEEDBACK_DURATION_MS = 2000;
function resolveCopyContent(content, contentOverride) {
    if (typeof contentOverride === 'string') {
        return contentOverride;
    }
    if (contentOverride === null) {
        return '';
    }
    return content ?? '';
}
async function writeTextToClipboard(content) {
    if (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(content);
            return true;
        }
        catch {
            // Fall through to the textarea fallback for browsers that expose the
            // Clipboard API but reject writes in the current context.
        }
    }
    return copyTextWithTextarea(content);
}
function copyTextWithTextarea(content) {
    if (typeof document === 'undefined' ||
        typeof document.createElement !== 'function' ||
        typeof document.execCommand !== 'function' ||
        !document.body) {
        return false;
    }
    const textArea = document.createElement('textarea');
    textArea.value = content;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.opacity = '0';
    try {
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange?.(0, textArea.value.length);
        return document.execCommand('copy');
    }
    catch {
        return false;
    }
    finally {
        textArea.parentNode?.removeChild(textArea);
    }
}
/**
 * Copies a string to the clipboard and exposes a transient `copied` flag
 * (auto-clears after `feedbackDurationMs`) so callers can show a confirmation
 * affordance.
 */
export function useCopyToClipboard(content, { feedbackDurationMs = DEFAULT_COPY_FEEDBACK_DURATION_MS } = {}) {
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef(undefined);
    const isMounted = useIsMounted();
    useEffect(() => () => clearTimeout(timeoutRef.current), []);
    const copy = useCallback(async (contentOverride) => {
        const didCopy = await writeTextToClipboard(resolveCopyContent(content, contentOverride));
        if (!didCopy) {
            return false;
        }
        if (!isMounted()) {
            return true;
        }
        setCopied(true);
        // Restart the feedback window on every copy so `copied` stays true for
        // `feedbackDurationMs` after the latest copy, not the first one.
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), feedbackDurationMs);
        return true;
    }, [content, feedbackDurationMs, isMounted]);
    const copySync = useCallback((contentOverride) => {
        void copy(contentOverride);
    }, [copy]);
    return { copied, copy, copySync };
}
//# sourceMappingURL=useCopyToClipboard.js.map